/*
 * BuildDocs пользовательский интерфейс version 0.1.0
 *
 * Copyright © 2021 ООО АДАПТ info@acfs.spb.ru
 * You may use, distribute and modify this code under the
 * terms of the ООО АДАПТ license
 */
import { User } from '@builddocs/data-management'
import { InvitationExternalSigning } from '@builddocs/feature-external-sign'
import {
  SignerInStampType,
  DocumentSigningTask,
  TemplateStampType,
  ApplicationKeys,
  Application,
} from '@builddocs/filestore-api'
import {
  statusColor,
  statusIcon,
  theStateWithDescription,
  UserInfoPopoverWithStatus,
} from '@builddocs/mui'
import { Stack, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import { format, parseISO } from 'date-fns'
import { ru } from 'date-fns/locale'

import SignPanel from './SignPanel'
import { BuilddocsOrganization } from './BuilddocsOrganization'

export const getTitleStamp = (title: string) => {
  switch (title) {
    case 'VALID_COPY':
      return 'КОПИЯ ВЕРНА'
    case 'IN_ACCORDANCE':
      return 'ВЫПОЛНЕНО В СООТВЕТСТВИИ С ПРОЕКТОМ'
    case 'TO_PRODUCTION':
      return 'В ПРОИЗВОДСТВО РАБОТ'
    case 'NO_DEVIATION':
      return 'ОТКЛОНЕНИЙ ОТ ПРОЕКТА НЕТ'
    case 'CORRESPONDS_TO_FACT':
      return 'СООТВЕТСТВУЕТ ФАКТУ'
    case 'NO_DEVIATION_PERMISSIBLE_VALUES':
      return 'ОТКЛОНЕНИЯ НЕ ПРЕВЫШАЮТ ДОПУСТИМЫХ ЗНАЧЕНИЙ'
    case 'DEVIATIONS_DO_NOT_AFFECT_LOAD_CAPACITY':
      return 'ОТКЛОНЕНИЯ НЕ ВЛИЯЮТ НА НЕСУЩУЮ СПОСОБНОСТЬ'
    case 'DEVIATIONS_DO_NOT_AFFECT_LOAD_CAPACITY_ARCHITECTURE_ENGINEERING':
      return 'ОТКЛОНЕНИЯ НЕ ВЛИЯЮТ НА НЕСУЩУЮ СПОСОБНОСТЬ, АРХИТЕКТУРНО-ПЛАНИРОВОЧНЫЕ РЕШЕНИЯ, ИНЖЕНЕРНЫЕ СИСТЕМЫ'
    case 'GEODESIC_SERVICE':
      return 'ГЕОДЕЗИЧЕСКАЯ СЛУЖБА'
    case 'NO_DEVIATION_GEODESIC_SERVICE':
      return 'ОТКЛОНЕНИЯ ОТ ПРОЕКТА НЕТ ГЕОДЕЗИЧЕСКАЯ СЛУЖБА'
    case 'APPROVED_AUTHOR_SERVICE':
      return 'СОГЛАСОВАНО АВТОРСКИМ НАДЗОРОМ'
    case 'CONFIRMED_CORRECTNESS_OF_INDICATED_DEVIATIONS':
      return 'ПРАВИЛЬНОСТЬ УКАЗАННЫХ ОТКЛОНЕНИЙ ПОДТВЕРЖДАЮ'
    case 'SIGNED':
      return 'ПОДПИСАНО:'
    case 'DEVIATIONS_EXCEEDING_NORMATIVE':
      return 'ИМЕЮТСЯ ОТКЛОНЕНИЯ ПРЕВЫШАЮЩИЕ НОРМАТИВНЫЕ'
    case 'AGREED_UPON_REGARDING_VERTICAL_DEVIATIONS':
      return 'СОГЛАСОВАНО В ЧАСТИ ВЕРТИКАЛЬНЫХ ОТКЛОНЕНИЙ'
    case 'DEVIATIONS_EXCEED_LIMITS_BUT_DO_NOT_AFFECT_LOAD_CAPACITY':
      return 'ИМЕЮТСЯ ОТКЛОНЕНИЯ ПРЕВЫШАЮЩИЕ НОРМАТИВНЫЕ. ОТКЛОНЕНИЯ НЕ ВЛИЯЮТ НА НЕСУЩУЮ СПОСОБНОСТЬ'
    case 'GEODETIC_GROUP_OF_THE_CUSTOMER_THE_MEASUREMENT_RESULTS_CORRESPOND_TO_THE_FACT':
      return 'ГЕОДЕЗИЧЕСКАЯ ГРУППА ЗАКАЗЧИКА\nРЕЗУЛЬТАТЫ ИЗМЕРЕНИЙ СООТВЕТСТВУЮТ ФАКТУ'
    case 'GEODETIC_GROUP_OF_THE_CUSTOMER_DEVIATIONS_FROM_THE_PROJECT_EXCEED_THE_LIMITS':
      return 'ГЕОДЕЗИЧЕСКАЯ ГРУППА ЗАКАЗЧИКА\nОТКЛОНЕНИЯ ОТ ПРОЕКТА ПРЕВЫШАЮТ ДОПУСТИМЫЕ'
    case 'GEODETIC_GROUP_OF_THE_CUSTOMER_CHECKED':
      return 'ГЕОДЕЗИЧЕСКАЯ ГРУППА ЗАКАЗЧИКА\nПРОВЕРЕНО'
    case 'THIS_DRAWING_IS_EXECUTIVE':
      return 'ДАННЫЙ ЧЕРТЁЖ ЯВЛЯЕТСЯ ИСПОЛНИТЕЛЬНЫМ'
    case 'AGREED_GIP':
      return 'СОГЛАСОВАНО ГИП'
    case 'AGREED_OPP_MAV':
      return 'Согласовано ОПП МАВ'
    default:
      return 'БЕЗ НАИМЕНОВАНИЯ'
  }
}

export interface StampViewerProps {
  stamp: TemplateStampType
  signingTask: DocumentSigningTask
  statusStamp: 'REJECTED' | 'PENDING' | 'ACCEPTED'
  docId: string
  documentType: ApplicationKeys
  userSigner: SignerInStampType | undefined
  user: User
  onUpdatePDF?: () => void
}

const StyledStampViewer = styled('div')({
  m: 1,
  borderStyle: 'solid',
  borderColor: `${props => props.theme.colors.blue}`,
  p: 1,
  backgroundColor: `${props => props.theme.colors.white}`,
  color: `${props => props.theme.colors.blue}`,
  minHeight: '70px',
  minWidth: '200px',
  cursor: 'pointer',
  userSelect: 'none',
})

export const StampViewer = ({
  stamp,
  signingTask,
  statusStamp,
  docId,
  documentType,
  userSigner,
  user,
  onUpdatePDF,
}: StampViewerProps) => {
  const dateSign = Math.max(
    ...signingTask.stampSigns.map(signer => Date.parse(signer.signingDateTime)),
  )

  return (
    <StyledStampViewer>
      <Stack spacing={1} sx={{ backgroundColor: 'white', m: 2 }}>
        <Typography
          color="primary"
          sx={{
            whiteSpace: 'pre-line',
          }}
          textAlign="center"
          variant="h6"
        >
          {stamp.name !== 'Error'
            ? getTitleStamp(stamp.name)
            : 'наименование штампа'}
        </Typography>
        {userSigner && (
          <SignPanel
            docId={docId}
            documentType={documentType}
            signingTask={signingTask}
            user={user}
            userSigner={userSigner}
            onUpdatePDF={onUpdatePDF}
          />
        )}
        {signingTask.stampSigns?.map(signer => (
          <Stack key={signer.userId} px={2} spacing={2}>
            <Stack direction="row" spacing={2}>
              <Typography color="primary">{signer.position}</Typography>
              <BuilddocsOrganization inn={signer.inn} />
            </Stack>
            <Stack direction="row" justifyContent="space-between" spacing={1}>
              <UserInfoPopoverWithStatus
                key={signer.userId}
                isFullName
                icon={statusIcon(signer.signatureState)}
                status={theStateWithDescription(signer.signatureState)}
                statusColor={statusColor(signer.signatureState)}
                userEmploymentId={signer.employmentId}
              />
              {documentType === Application.EXECUTIVE_SCHEME && (
                <InvitationExternalSigning
                  iconButton
                  assignees={[signer]}
                  documentId={docId}
                  signType={Application.EXECUTIVE_SCHEME}
                  taskId={signingTask.id}
                />
              )}
            </Stack>
            <Typography fontWeight="bold" variant="body2">
              {signer.signatureState === 'PENDING'
                ? 'Ожидает подписания'
                : signer.signatureState === 'SIGNED'
                ? `Подписано электронной цифровой подписью ${
                    signer.signingDateTime &&
                    format(parseISO(signer.signingDateTime), 'd MMM yyyy г.', {
                      locale: ru,
                    })
                  }`
                : signer.signatureState === 'REJECTED'
                ? `Отклонено. Комментарий: ${signer.comment}`
                : ' Не подписано'}
            </Typography>
          </Stack>
        ))}
        {statusStamp === 'ACCEPTED' && (
          <Typography color="primary" textAlign="center">
            Дата:{' '}
            {format(dateSign, 'd MMM yyyy г.', {
              locale: ru,
            })}
          </Typography>
        )}
      </Stack>
    </StyledStampViewer>
  )
}

export default StampViewer
